"use client"
import { useEffect, useState } from "react"
import Link from "next/link"
import { X, SlidersHorizontal } from "lucide-react"
import { cn } from "@/lib/utils"

type Props = {
  disciplines: string[]
  current?: string
}

export function TopRatedFilters({ disciplines, current }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false)

  useEffect(() => {
    if (sheetOpen) {
      const original = document.body.style.overflow
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = original }
    }
  }, [sheetOpen])

  const chipBase = "px-3 py-1.5 text-[13px] rounded-md border transition-colors whitespace-nowrap"
  const chipActive = "bg-[var(--foreground)] text-white border-[var(--foreground)]"
  const chipInactive = "bg-white border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]"

  const chips = (
    <>
      <Link
        href="/top-rated"
        className={cn(chipBase, !current ? chipActive : chipInactive)}
        onClick={() => setSheetOpen(false)}
      >
        All
      </Link>
      {disciplines.map((d) => (
        <Link
          key={d}
          href={`/top-rated?discipline=${encodeURIComponent(d)}`}
          className={cn(chipBase, current === d ? chipActive : chipInactive)}
          onClick={() => setSheetOpen(false)}
        >
          {d}
        </Link>
      ))}
    </>
  )

  return (
    <>
      {/* Mobile: single button → sheet */}
      <div className="md:hidden">
        <button
          onClick={() => setSheetOpen(true)}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border transition-colors",
            current
              ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
              : "bg-white text-[var(--muted-foreground)] border-[var(--border)]"
          )}
        >
          <SlidersHorizontal size={13} />
          {current ? `Filter: ${current}` : "Filter by discipline"}
        </button>
      </div>

      {/* Desktop: horizontal scroll row (was flex-wrap; now scrolls to save vertical space) */}
      <div className="hidden md:flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
        {chips}
      </div>

      {/* Mobile sheet */}
      {sheetOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Filter by discipline"
        >
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="flex-1 bg-black/40 backdrop-blur-sm animate-fade-in"
            aria-label="Close"
          />
          <div className="bg-white rounded-t-2xl max-h-[85vh] flex flex-col animate-slide-up-sheet shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
              <h3 className="text-[15px] font-semibold">Filter by discipline</h3>
              <button
                onClick={() => setSheetOpen(false)}
                className="p-1 -mr-1 rounded-md hover:bg-[var(--muted)] transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="flex flex-wrap gap-1.5">
                {chips}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
